import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Settings } from 'lucide-react';
import { useId } from 'react';
import { Controller, type UseFormRegisterReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FieldError } from '@/components/ui/field-error';
import { BROKER_SETTING_OPTIONS, BROKER_SETTING_VALUES } from '@/features/brokers/list/broker-options';
import { brokerDetailQueryKey, brokerTradingStatsQueryKey } from '@/hooks/queries/brokers/use-broker-detail';
import { BROKERS_LIST_QUERY_KEY } from '@/hooks/queries/brokers/use-brokers';
import { getErrorMessage } from '@/lib/api/error';
import { buildUpdateBrokerSettingsRequest, type FeeSplit, updateBrokerSettings, type WireBroker } from '@/services/api/brokers';

const PERCENT_MIN = 0;
const PERCENT_MAX = 100;
const BPS_MIN = -100;
const BPS_MAX = 100;
const FEE_STEP = 0.01;

const PERCENT_RANGE_MESSAGE = 'Must be between 0 and 100';
const BPS_RANGE_MESSAGE = 'Must be between -100 and 100';
const DECIMALS_MESSAGE = 'Maximum 2 decimal places';

const TWO_DECIMALS_PATTERN = /^-?\d+(\.\d{1,2})?$/;

const FEE_SPLIT_TYPES = ['percentage', 'basis_points'] as const;

const editSettingsSchema = z
  .object({
    settings: z.array(z.enum(BROKER_SETTING_VALUES)),
    feeSplitType: z.enum(FEE_SPLIT_TYPES),
    trades: z.string(),
    liquidations: z.string(),
    makerFeeBps: z.string(),
    takerFeeBps: z.string(),
  })
  // Deliberate fix: legacy showed field errors but never
  // gated Save — out-of-range values went to the API and died as a generic 500.
  // Rules run per selected fee-split type so a hidden branch's leftovers can't
  // block (or fail) a save. Messages verbatim; empty fields are allowed.
  .superRefine((values, context) => {
    const validateBounded = (field: 'trades' | 'liquidations' | 'makerFeeBps' | 'takerFeeBps', min: number, max: number, rangeMessage: string) => {
      const raw = values[field];
      if (raw === '') return;
      const parsed = Number(raw);
      if (Number.isNaN(parsed) || parsed < min || parsed > max) {
        context.addIssue({ code: 'custom', path: [field], message: rangeMessage });
        return;
      }
      if (!TWO_DECIMALS_PATTERN.test(raw)) context.addIssue({ code: 'custom', path: [field], message: DECIMALS_MESSAGE });
    };
    if (values.feeSplitType === 'percentage') {
      validateBounded('trades', PERCENT_MIN, PERCENT_MAX, PERCENT_RANGE_MESSAGE);
    } else {
      validateBounded('makerFeeBps', BPS_MIN, BPS_MAX, BPS_RANGE_MESSAGE);
      validateBounded('takerFeeBps', BPS_MIN, BPS_MAX, BPS_RANGE_MESSAGE);
    }
    validateBounded('liquidations', PERCENT_MIN, PERCENT_MAX, PERCENT_RANGE_MESSAGE);
  });

type EditSettingsFormValues = z.infer<typeof editSettingsSchema>;

/**
 * Prefill: type defaults to percentage; liquidations comes
 * from either type; bps prefills maker/taker and leaves trades empty; percentage
 * prefills trades and leaves maker/taker empty.
 */
const toFormValues = (broker: WireBroker): EditSettingsFormValues => {
  const feeSplit = broker.fee_split;
  return {
    settings: [...(broker.settings ?? [])],
    feeSplitType: feeSplit?.type ?? 'percentage',
    trades: feeSplit?.type === 'percentage' && feeSplit.trades !== undefined ? String(feeSplit.trades) : '',
    liquidations: feeSplit?.liquidations !== undefined ? String(feeSplit.liquidations) : '',
    makerFeeBps: feeSplit?.type === 'basis_points' ? String(feeSplit.maker_fee_bps) : '',
    takerFeeBps: feeSplit?.type === 'basis_points' ? String(feeSplit.taker_fee_bps) : '',
  };
};

/** Legacy `isValidNumber`: not undefined, not '', not NaN. */
const isValidNumber = (value: string): boolean => value !== '' && !Number.isNaN(Number(value));

/**
 * Fee-split payload, legacy logic. Two load-bearing
 * quirks preserved on purpose:
 * - Empty bps fields COERCE TO 0 — the wire schema requires both; the inputs
 *   show placeholder "0" so the effective value is visible.
 * - A percentage split with no rates still sends `{ type: 'percentage' }` (wire
 *   parity — the API has no explicit clear); renderers show it as
 *   "No rates configured".
 */
const buildFeeSplit = (values: EditSettingsFormValues): FeeSplit => {
  if (values.feeSplitType === 'basis_points') {
    return {
      type: 'basis_points',
      maker_fee_bps: isValidNumber(values.makerFeeBps) ? Number(values.makerFeeBps) : 0,
      taker_fee_bps: isValidNumber(values.takerFeeBps) ? Number(values.takerFeeBps) : 0,
      ...(isValidNumber(values.liquidations) ? { liquidations: Number(values.liquidations) } : {}),
    };
  }
  return {
    type: 'percentage',
    ...(isValidNumber(values.trades) ? { trades: Number(values.trades) } : {}),
    ...(isValidNumber(values.liquidations) ? { liquidations: Number(values.liquidations) } : {}),
  };
};

const FieldHint = ({ children }: { children: string }) => <p className="text-xs text-muted-foreground">{children}</p>;
FieldHint.displayName = 'FieldHint';

interface NumberFieldProps {
  id: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  placeholder?: string;
  error?: string;
  registration: UseFormRegisterReturn;
}

const NumberField = ({ id, label, hint, min, max, placeholder, error, registration }: NumberFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} inputMode="decimal" max={max} min={min} placeholder={placeholder} step={FEE_STEP} type="number" {...registration} />
    <FieldError message={error} />
    <FieldHint>{hint}</FieldHint>
  </div>
);
NumberField.displayName = 'NumberField';

interface EditSettingsDialogProps {
  /** The freshest broker data — the caller mounts this dialog per open */
  broker: WireBroker;
  onClose: () => void;
}

export const EditSettingsDialog = ({ broker, onClose }: EditSettingsDialogProps) => {
  const fieldId = useId();
  const queryClient = useQueryClient();

  const form = useForm<EditSettingsFormValues>({
    resolver: zodResolver(editSettingsSchema),
    mode: 'onChange',
    defaultValues: toFormValues(broker),
  });

  const selectedSettings = form.watch('settings');
  const feeSplitType = form.watch('feeSplitType');

  const toggleSetting = (value: (typeof BROKER_SETTING_VALUES)[number], isChecked: boolean) => {
    const next = isChecked ? [...selectedSettings, value] : selectedSettings.filter((setting) => setting !== value);
    form.setValue('settings', next, { shouldDirty: true, shouldValidate: true });
  };

  const handleSave = form.handleSubmit(async (values) => {
    const justification = await confirmAction({
      title: 'Confirm Update broker settings',
      description: `Proceeding with this action will update the settings for broker ${broker.name}. Your action here will be logged and will be auditable.`,
      actionLabel: 'Update broker settings',
      onConfirm: async (comment) => {
        try {
          // Full-state replace: `settings` is the complete desired list and a
          // feeSplit object is ALWAYS sent (legacy parity).
          await updateBrokerSettings(
            buildUpdateBrokerSettingsRequest({
              brokerId: broker.s,
              settings: values.settings,
              feeSplit: buildFeeSplit(values),
              comment,
            }),
          );
        } catch (error) {
          // Surface real upstream codes (e.g. FEE_SPLIT_MAX_2_DECIMALS) instead of
          // the legacy generic toast; rethrow keeps the
          // audit dialog open and recoverable.
          throw new Error(`Failed to update broker settings: ${getErrorMessage(error)}`);
        }
      },
    });
    if (justification === null) return; // cancelled — the settings dialog stays open

    toast.success('Broker settings updated successfully');
    // Refetch the broker (chips + overview), its fee-split-bearing trading stats,
    // and the list page cards.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: brokerDetailQueryKey(broker.s), exact: true }),
      queryClient.invalidateQueries({ queryKey: brokerTradingStatsQueryKey(broker.s) }),
      queryClient.invalidateQueries({ queryKey: BROKERS_LIST_QUERY_KEY }),
    ]);
    onClose();
  });

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      {/* Persistent like the legacy dialog: no backdrop or Esc dismiss — Cancel/Save only */}
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-[700px]"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="size-5" />
            Edit Broker Settings
          </DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4" noValidate onSubmit={handleSave}>
          {/* Same 5 checkboxes, identical copy to the New Broker dialog */}
          <div className="flex flex-col gap-3">
            {BROKER_SETTING_OPTIONS.map((option) => (
              <div className="flex items-start gap-3" key={option.value}>
                <Checkbox
                  checked={selectedSettings.includes(option.value)}
                  id={`${fieldId}-${option.value}`}
                  onCheckedChange={(checked) => toggleSetting(option.value, checked === true)}
                />
                <div className="flex flex-col gap-0.5">
                  <Label className="cursor-pointer" htmlFor={`${fieldId}-${option.value}`}>
                    {option.title}
                  </Label>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
            ))}
          </div>

          <Separator />

          <Controller
            control={form.control}
            name="feeSplitType"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-fee-split-type`}>Fee Split Type</Label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger id={`${fieldId}-fee-split-type`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="basis_points">Basis Points</SelectItem>
                  </SelectContent>
                </Select>
                <FieldHint>How the broker&apos;s revenue share is calculated.</FieldHint>
              </div>
            )}
          />

          {feeSplitType === 'percentage' ? (
            <NumberField
              error={form.formState.errors.trades?.message}
              hint="Broker's share of trade fees (0–100). Leave empty if OT keeps 100%."
              id={`${fieldId}-trades`}
              label="Trades Fee Split (%)"
              max={PERCENT_MAX}
              min={PERCENT_MIN}
              registration={form.register('trades')}
            />
          ) : (
            <>
              <NumberField
                error={form.formState.errors.makerFeeBps?.message}
                hint="OT's maker fee in basis points. Negative = rebate (OT pays broker)."
                id={`${fieldId}-maker-bps`}
                label="OT Maker Fee (bps)"
                max={BPS_MAX}
                min={BPS_MIN}
                placeholder="0"
                registration={form.register('makerFeeBps')}
              />
              <NumberField
                error={form.formState.errors.takerFeeBps?.message}
                hint="OT's taker fee in basis points."
                id={`${fieldId}-taker-bps`}
                label="OT Taker Fee (bps)"
                max={BPS_MAX}
                min={BPS_MIN}
                placeholder="0"
                registration={form.register('takerFeeBps')}
              />
            </>
          )}
          <NumberField
            error={form.formState.errors.liquidations?.message}
            hint="Broker's share of liquidation fees (0–100). Leave empty if OT keeps 100%."
            id={`${fieldId}-liquidations`}
            label="Liquidations Fee Split (%)"
            max={PERCENT_MAX}
            min={PERCENT_MIN}
            registration={form.register('liquidations')}
          />

          <DialogFooter>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            {/* Deliberate fix: Save is gated on client-side validity */}
            <Button disabled={!form.formState.isValid} type="submit" variant="primary">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
EditSettingsDialog.displayName = 'EditSettingsDialog';
